import React, { useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { AccentTextUnbound } from '../unbound/AccentTextUnBound';
import { AccentComboUnbound } from '../unbound/AccentComboUnbound';
import { accentUtils } from '../../services/HelperService';

// Keep in sync with PreparedSignatureStyles in DocumentActions.cs.
const SIGNATURE_STYLES = [
    { ID: "Style_Default", Name: "Default", FontName: "Segoe UI", fontSize: 14, fontStyle: "normal" },
    { ID: "Style_Script", Name: "Script", FontName: "Segoe Script", fontSize: 16, fontStyle: "italic" },
    { ID: "Style_Serif", Name: "Serif", FontName: "Times New Roman", fontSize: 16, fontStyle: "italic" }
];

const getSignatureStyle = (styleId) => SIGNATURE_STYLES.find(s => s.ID === styleId) ?? SIGNATURE_STYLES[0];

const escapeSvgText = (text) => String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const toBase64 = (value) => btoa(encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (match, hex) => String.fromCharCode(parseInt(hex, 16))));

const createSignatureSvgDataUrl = (text, signatureStyle) => {
    const svg = `
<svg xmlns="http://www.w3.org/2000/svg" width="500" height="200" viewBox="0 0 500 200">
  <text x="250" y="110" text-anchor="middle" dominant-baseline="middle" font-family="${signatureStyle.FontName}" font-size="${signatureStyle.fontSize * 3}" font-style="${signatureStyle.fontStyle}" fill="black">${escapeSvgText(text)}</text>
</svg>`.trim();

    return `data:image/svg+xml;base64,${toBase64(svg)}`;
};

// Generates default initials from a full name. User can override in the UI.
const generateInitials = (name) => {
    if (!name) return "";

    // Strip trailing "(...)" contact details if present
    const cleaned = String(name).replace(/\s*\([^()]*\)\s*$/, '').trim();
    if (!cleaned) return "";

    const parts = cleaned
        .split(/\s+/)
        .map(p => p.trim())
        .filter(Boolean);

    // Take first letter of up to 3 words (e.g., "John Q Public" -> "JQP")
    return parts
        .slice(0, 3)
        .map(p => (p[0] ? p[0].toUpperCase() : ""))
        .join("");
};

export const RepSignaturePad = ({ onSignatureSave, readOnly, onCancel, imageType = 'image/svg+xml', previewSignature }) => {
    const sigCanvas = useRef({});
    const [showAccept, setShowAccept] = React.useState(false);
    const [signatureEntryMode, setSignatureEntryMode] = useState("draw");
    const [typedSignature, setTypedSignature] = useState("");
    const [signatureStyleId, setSignatureStyleId] = useState(SIGNATURE_STYLES[0].ID);
    const signatureStyle = useMemo(() => getSignatureStyle(signatureStyleId), [signatureStyleId]);

    useEffect(() => {
        if (accentUtils.isEmpty(previewSignature) || accentUtils.isNull(sigCanvas.current?.fromDataURL)) return;

        sigCanvas.current.clear();
        sigCanvas.current.fromDataURL(previewSignature);
    }, [previewSignature]);

    useEffect(() => {
        if (signatureEntryMode === "type") {
            setShowAccept(typedSignature.trim().length > 0);
        }
    }, [signatureEntryMode, typedSignature]);

    const clear = () => {
        if (signatureEntryMode === "type") {
            setTypedSignature("");
        } else {
            sigCanvas.current.clear();
        }
        setShowAccept(false);
    };

    const save = () => {
        if (signatureEntryMode === "type") {
            const signatureText = typedSignature.trim();

            onSignatureSave(
                signatureText ? createSignatureSvgDataUrl(signatureText, signatureStyle) : null,
                undefined,
                undefined,
                signatureStyleId
            );
            return;
        }

        const isEmpty = sigCanvas.current.isEmpty();

        if (isEmpty) {
            onSignatureSave(null, undefined, undefined);
            return;
        }

        const dataUrl = sigCanvas.current.toDataURL(imageType);
        onSignatureSave(dataUrl, undefined, undefined);
    };

    const style = readOnly ? { pointerEvents: 'none' } : undefined;
    const btnStyle = { minWidth: "80px" };
    const modeBtnStyle = selected => ({
        ...btnStyle,
        fontWeight: selected ? "bold" : undefined
    });

    const onSignatureEntryModeChanged = mode => {
        setSignatureEntryMode(mode);
        setShowAccept(mode === "type" ? typedSignature.trim().length > 0 : false);
    };

    return (
        <div style={style}>
            {accentUtils.isEmpty(previewSignature) && <div className="d-flex align-items-center justify-content-between" style={{ width: 500, height: 38, gap: 8, marginBottom: 8 }}>
                <div className="d-flex" style={{ gap: 8 }}>
                    <button
                        style={modeBtnStyle(signatureEntryMode === "draw")}
                        disabled={readOnly}
                        onClick={() => onSignatureEntryModeChanged("draw")}
                    >
                        Draw
                    </button>
                    <button
                        style={modeBtnStyle(signatureEntryMode === "type")}
                        disabled={readOnly}
                        onClick={() => onSignatureEntryModeChanged("type")}
                    >
                        Type
                    </button>
                </div>
                {signatureEntryMode === "type" && (
                    <div style={{ width: 200 }}>
                        <AccentComboUnbound
                            displayField="FontName"
                            valueField="ID"
                            defaultValue={signatureStyleId}
                            data={SIGNATURE_STYLES}
                            disabled={readOnly}
                            hideClear
                            onChange={v => setSignatureStyleId(v ?? SIGNATURE_STYLES[0].ID)}
                        />
                    </div>
                )}
            </div>}
            {!accentUtils.isEmpty(previewSignature) || signatureEntryMode === "draw" ? (
                <SignatureCanvas
                    ref={sigCanvas}
                    penColor="black"
                    canvasProps={{
                        width: 500,
                        height: 200,
                        className: 'signature-pad',
                        style: {
                            border: '1px solid #d9d9d9',
                            borderRadius: '2px'
                        }
                    }}
                    onBegin={e => {
                        if (accentUtils.isEmpty(previewSignature)) {
                            setShowAccept(true);
                        }
                    }}
                />
            ) : (
                <div
                    className="signature-pad"
                    style={{
                        width: 502,
                        height: 202,
                        boxSizing: 'border-box',
                        border: '1px solid #d9d9d9',
                        borderRadius: '2px',
                        display: 'flex',
                        margin: '6px 0'
                    }}
                >
                    <AccentTextUnbound
                        defaultValue={typedSignature}
                        disabled={readOnly}
                        dataTagName="repSignatureText"
                        resetWhenDefaultValueChanges
                        style={{
                            width: '100%',
                            height: '100%',
                            boxSizing: 'border-box',
                            border: 'none',
                            borderRadius: 0,
                            display: 'block',
                            color: 'black',
                            fontFamily: signatureStyle.FontName,
                            fontSize: signatureStyle.fontSize * 3,
                            fontStyle: signatureStyle.fontStyle,
                            textAlign: 'center',
                            margin: 0,
                            padding: '0px'
                        }}
                        onChange={v => setTypedSignature(v ?? "")}
                    />
                </div>
            )}
            {!readOnly && <div className="d-flex justify-content-between">
                <div><button style={ btnStyle} onClick={onCancel}>Cancel</button></div>
                <div><button style={btnStyle} onClick={clear}>Clear</button></div>
                <div>{<button style={btnStyle} onClick={save} disabled={!showAccept}>Accept</button>}</div>
            </div>}
        </div>
    );
};

export const SignaturePad = ({ onSignatureSave, readOnly, onCancel, signer, imageType = 'image/png' }) => {
    const sigCanvas = useRef({});

    const [showAccept, setShowAccept] = React.useState(false);

    // Show signer field only if the prop was actually provided
    const showSigner = typeof signer !== 'undefined';

    // AccentTextUnbound is "unbound" (uses defaultValue once), so keep local state and key it for refresh on prop changes
    const [signerName, setSignerName] = useState(signer ?? "");
    useEffect(() => setSignerName(signer ?? ""), [signer]);
    const signerKey = useMemo(() => `signer_${signer ?? ""}`, [signer]);

    // Initials: default from signer, but user can edit
    const [initials, setInitials] = useState(generateInitials(signer));
    useEffect(() => setInitials(generateInitials(signer)), [signer]);
    const initialsKey = useMemo(() => `initials_${signer ?? ""}`, [signer]);

    const clear = () => {
        sigCanvas.current.clear();
        setShowAccept(false);
    };

    const save = () => {

        const isEmpty = sigCanvas.current.isEmpty();

        if (isEmpty) {
            onSignatureSave(
                null,
                showSigner ? signerName : undefined,
                showSigner ? initials : undefined
            );
            return;
        }

        const dataUrl = sigCanvas.current.toDataURL(imageType);

        // 2nd arg is backwards compatible: existing handlers ignore it
        onSignatureSave(
            dataUrl,
            showSigner ? signerName : undefined,
            showSigner ? initials : undefined
        );
    };


    const style = readOnly ? { pointerEvents: 'none' } : undefined;

    const btnStyle = { minWidth: "80px" };

    return (
        <div style={style}>
            {showSigner && (
                <div style={{ width: 500, marginBottom: 8 }}>
                    <div className="d-flex" style={{ gap: 8 }}>
                        <div style={{ flex: 1 }}>
                            <AccentTextUnbound
                                key={signerKey}
                                defaultValue={signerName}
                                label="Signer name"
                                disabled={readOnly}
                                onChange={v => setSignerName(v ?? "")}
                            />
                        </div>
                        <div style={{ width: 120 }}>
                            <AccentTextUnbound
                                key={initialsKey}
                                defaultValue={initials}
                                label="Initials"
                                disabled={readOnly}
                                maxLength={6}
                                onChange={v => setInitials((v ?? "").toUpperCase())}
                            />
                        </div>
                    </div>
                </div>
            )}
            <SignatureCanvas
                ref={sigCanvas}
                penColor="black"
                canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-pad',
                    style: {
                        border: '1px solid #d9d9d9',
                        borderRadius: '2px'
                    }
                }}
                onBegin={e => setShowAccept(true) }
            />
            {!readOnly && <div className="d-flex justify-content-between">
                <div><button style={ btnStyle} onClick={onCancel}>Cancel</button></div>
                <div><button style={btnStyle} onClick={clear}>Clear</button></div>
                <div>{<button style={btnStyle} onClick={save} disabled={!showAccept}>Accept</button>}</div>
            </div>}
        </div>
    );
};


